import { useState } from "react";
import { Loader2, Upload, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fileUrl, formatApiError } from "@/lib/api";

const EMPTY = { title_en: "", title_mh: "", description_en: "", description_mh: "", speaker: "", scripture: "", category: "sermon", date: "", duration: "", audio_url: "", video_url: "", cover_url: "", attachments: [] };
const field = "w-full rounded-xl border border-[#F4EEE2]/15 bg-[#0B111E] px-3 py-2.5 text-sm text-[#F4EEE2] outline-none focus:border-[#D4AF37]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-ui text-[11px] uppercase tracking-widest text-[#B7BFCC]">{label}</span>
    {children}
  </label>
);

const CHUNK = 5 * 1024 * 1024;

// Uploads a file in 5MB pieces so big videos and sermons get through. Returns the file URL.
export const uploadFile = async (file, onProgress, library = false) => {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const id = crypto.randomUUID();
  let data;
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    Object.entries({ upload_id: id, index: i, total, filename: file.name, content_type: file.type || "application/octet-stream", library }).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    ({ data } = await api.post("/uploads/chunk", fd));
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  return data.url;
};

export const UploadBtn = ({ accept, onDone, testid, label, multiple = false, library = false }) => {
  const [pct, setPct] = useState(null);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      for (const [n, f] of files.entries()) {
        const url = await uploadFile(f, (p) => setPct(files.length > 1 ? `${n + 1}/${files.length} · ${p}` : p), library);
        onDone(url, f);
      }
      toast.success(files.length > 1 ? `${files.length} files uploaded` : "Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPct(null);
      e.target.value = "";
    }
  };
  return (
    <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 font-ui text-xs font-semibold text-[#F4EEE2] hover:bg-[#D4AF37]/10">
      {pct !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {pct !== null ? <span data-testid={`${testid}-progress`}>{pct}%</span> : label}
      <input data-testid={testid} type="file" accept={accept} multiple={multiple} onChange={onChange} disabled={pct !== null} className="hidden" />
    </label>
  );
};

export const SermonForm = ({ open, onClose, initial, onSaved, speakers = [] }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]]));
      const res = initial?.id ? await api.put(`/sermons/${initial.id}`, body) : await api.post("/sermons", body);
      if (res.data?.pending) toast("Sent for approval — it will appear once a Super Admin approves it");
      else toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent data-testid="sermon-form-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-[#D4AF37]/20 bg-[#131C2E] text-[#F4EEE2]">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{initial?.id ? "Edit message" : "New message"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <Field label="Title (English)"><input data-testid="sermon-form-title-en" required value={f.title_en} onChange={set("title_en")} className={field} /></Field>
          <Field label="Title (Marshallese)"><input data-testid="sermon-form-title-mh" value={f.title_mh} onChange={set("title_mh")} className={field} /></Field>
          <Field label="Description (English)"><textarea data-testid="sermon-form-desc-en" rows={3} value={f.description_en} onChange={set("description_en")} className={field} /></Field>
          <Field label="Description (Marshallese)"><textarea data-testid="sermon-form-desc-mh" rows={3} value={f.description_mh} onChange={set("description_mh")} className={field} /></Field>
          <Field label="Pastor / Speaker"><input data-testid="sermon-form-speaker" list="sermon-speakers" value={f.speaker} onChange={set("speaker")} className={field} /><datalist id="sermon-speakers">{speakers.map((s) => <option key={s} value={s} />)}</datalist></Field>
          <Field label="Scripture"><input data-testid="sermon-form-scripture" value={f.scripture} onChange={set("scripture")} className={field} /></Field>
          <Field label="Category">
            <select data-testid="sermon-form-category" value={f.category} onChange={set("category")} className={field}>
              <option value="sermon">Sermon</option>
              <option value="devotional">Devotional</option>
              <option value="music">Gospel Music</option>
              <option value="youth">Youth</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><input data-testid="sermon-form-date" type="date" value={f.date} onChange={set("date")} className={field} /></Field>
            <Field label="Duration"><input data-testid="sermon-form-duration" placeholder="40:00" value={f.duration} onChange={set("duration")} className={field} /></Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Cover photo">
              <div className="flex items-center gap-4">
                {f.cover_url && <img src={fileUrl(f.cover_url)} alt="" className="h-16 w-14 rounded-t-full object-cover" data-testid="sermon-form-cover-preview" />}
                <UploadBtn accept="image/*" testid="sermon-form-cover-upload" label={f.cover_url ? "Replace photo" : "Upload photo"} onDone={(u) => setF((p) => ({ ...p, cover_url: u }))} />
                {f.cover_url && (
                  <button type="button" data-testid="sermon-form-cover-remove" onClick={() => setF((p) => ({ ...p, cover_url: "" }))} className="flex items-center gap-1.5 rounded-full px-3 py-2 font-ui text-xs text-[#F2A08A] hover:bg-[#F2A08A]/10">
                    <Trash2 className="h-3.5 w-3.5" /> Remove photo
                  </button>
                )}
              </div>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Audio (upload a file or paste a link)">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input data-testid="sermon-form-audio-url" placeholder="https://…" value={f.audio_url} onChange={set("audio_url")} className={field} />
                <UploadBtn accept="audio/*" testid="sermon-form-audio-upload" label="Upload audio" onDone={(u) => setF((p) => ({ ...p, audio_url: u }))} />
                {f.audio_url && (
                  <button type="button" data-testid="sermon-form-audio-remove" onClick={() => setF((p) => ({ ...p, audio_url: "" }))} className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 font-ui text-xs text-[#F2A08A] hover:bg-[#F2A08A]/10">
                    <Trash2 className="h-3.5 w-3.5" /> Remove audio
                  </button>
                )}
              </div>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Video (optional: upload a file or paste a YouTube/Facebook link)">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input data-testid="sermon-form-video-url" placeholder="https://youtube.com/…" value={f.video_url || ""} onChange={set("video_url")} className={field} />
                <UploadBtn accept="video/mp4,video/quicktime,video/webm" testid="sermon-form-video-upload" label="Upload video" onDone={(u) => setF((p) => ({ ...p, video_url: u }))} />
                {f.video_url && (
                  <button type="button" data-testid="sermon-form-video-remove" onClick={() => setF((p) => ({ ...p, video_url: "" }))} className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 font-ui text-xs text-[#F2A08A] hover:bg-[#F2A08A]/10">
                    <Trash2 className="h-3.5 w-3.5" /> Remove video
                  </button>
                )}
              </div>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Files for visitors (PDF, JPEG, PNG: sermon notes, outline, flyer)">
              <div className="grid gap-2">
                {(f.attachments || []).map((a, i) => (
                  <div key={a.url} data-testid={`sermon-form-attachment-${i}`} className="flex items-center gap-2 rounded-xl border border-[#F4EEE2]/10 px-3 py-2">
                    <FileText className="h-4 w-4 shrink-0 text-[#D4AF37]" />
                    <input value={a.name} onChange={(e) => setF((p) => ({ ...p, attachments: p.attachments.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) }))} className="min-w-0 flex-1 bg-transparent text-sm text-[#F4EEE2] outline-none" aria-label="File name" />
                    <button type="button" data-testid={`sermon-form-attachment-remove-${i}`} onClick={() => setF((p) => ({ ...p, attachments: p.attachments.filter((_, j) => j !== i) }))} className="rounded-full p-1.5 text-[#F2A08A] hover:bg-[#F2A08A]/10" aria-label="Remove file"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <div><UploadBtn multiple accept="application/pdf,image/jpeg,image/png" testid="sermon-form-attachment-upload" label="Add files" onDone={(u, file) => setF((p) => ({ ...p, attachments: [...(p.attachments || []), { url: u, name: file.name, type: file.type }] }))} /></div>
              </div>
            </Field>
          </div>
          <button data-testid="admin-save-sermon-btn" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 font-ui font-semibold text-[#0B111E] disabled:opacity-60 sm:col-span-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
